import { useEffect } from 'react';
import { ConnectDragSource, DragSourceMonitor, useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useMeasure } from 'react-use';
import { UseMeasureRef } from 'react-use/lib/useMeasure';
import { useFeatureFlag } from 'modules/common/hooks';
import { TimeSignal } from 'graphql/types';
import { itemTypes } from 'modules/dragAndDrop/itemTypes';

interface UseNotesListItemDragProps {
  note: TimeSignal;
  groupIndex: string;
  isItemUncategorized: boolean;
  isItemGrouped: boolean;
  isItemScrolled: boolean;
}

interface UseNotesListItemDragReturnProps {
  isDragging: boolean;
  dragRef: ConnectDragSource;
  useMeasureRef: UseMeasureRef<HTMLElement> | undefined;
}

export const useNotesListItemDrag = ({
  note,
  groupIndex,
  isItemUncategorized,
  isItemGrouped,
  isItemScrolled
}: UseNotesListItemDragProps): UseNotesListItemDragReturnProps => {
  const isTIWebGroupViewEnabled: boolean = useFeatureFlag(
    'isTIWebGroupViewEnabled',
    false
  );

  const [useMeasureRef, { width, height }] = useMeasure();

  const [{ isDragging }, dragRef, dragPreview] = useDrag({
    type: itemTypes.NOTE_LIST_ITEM,
    item: () => ({
      note: note,
      groupIndex: groupIndex,
      day: note.date,
      isItemUncategorized: isItemUncategorized,
      isItemScrolled: isItemScrolled,
      width: width,
      height: height
    }),
    canDrag: () => isItemGrouped && isTIWebGroupViewEnabled,
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  });

  return {
    isDragging: isDragging,
    dragRef: dragRef,
    useMeasureRef: isTIWebGroupViewEnabled ? useMeasureRef : undefined
  };
};
